import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import clientApi from '../lib/clientApi';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { 
  Plus, MessageSquare, Send, Image, Paperclip, X, 
  CheckCircle, Clock, AlertCircle, Loader2, ArrowLeft,
  Upload
} from 'lucide-react';
import { HeaderSkeleton, TableSkeleton } from './skeletons/Skeletons';

export default function ClientHelpDeskPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    attachments: []
  });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedTicket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTickets = async () => {
    try {
      const res = await clientApi.get('/api/client/tickets');
      setTickets(res.data || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await clientApi.get(`/api/client/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleFileUpload = async (e, isForMessage = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await clientApi.post('/api/files/upload-s3', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (isForMessage && uploadedUrls.length > 0) {
      await sendReply(newMessage || 'Attached file(s)', uploadedUrls);
    } else {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedUrls]
      }));
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.description) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const res = await clientApi.post('/api/client/tickets', formData);
      toast.success('Ticket created successfully!');
      setShowCreateForm(false);
      setFormData({ subject: '', description: '', priority: 'medium', attachments: [] });
      fetchTickets();
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const sendReply = async (message, attachments = []) => {
    if (!message.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await clientApi.post(`/api/client/tickets/${selectedTicket._id}/reply`, {
        message: message.trim(),
        attachments
      });
      setNewMessage('');
      fetchTicketDetails(selectedTicket._id);
      toast.success('Reply sent');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    sendReply(newMessage);
  };

  const handleResolveTicket = async () => {
    try {
      await clientApi.put(`/api/client/tickets/${selectedTicket._id}/status`, {
        status: 'resolved'
      });
      toast.success('Ticket marked as resolved');
      fetchTickets();
      fetchTicketDetails(selectedTicket._id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-neutral-100 text-[#1a1a2e] border-white/40'
    };
    return styles[status] || styles.open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-neutral-100 text-neutral-600',
      medium: 'bg-orange-100 text-orange-600',
      high: 'bg-red-100 text-red-600'
    };
    return styles[priority] || styles.medium;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="client-help-desk-loading">
        <HeaderSkeleton />
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  // Ticket Detail View (Chat Interface)
  if (selectedTicket) {
    return (
      <div className="h-[calc(100vh-80px)] flex flex-col bg-white/40">
        {/* Header */}
        <div className="bg-white border-b border-white/40 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-neutral-600" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-[#1a1a2e]">{selectedTicket.subject}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(selectedTicket.status)}`}>
                    {selectedTicket.status?.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
              </div>
            </div>
            {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
              <button
                onClick={handleResolveTicket}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Resolved
              </button>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Initial Ticket */}
          <div className="bg-white rounded-xl p-4 shadow-soft border border-blue-200 max-w-3xl">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-medium text-sm">
                  {selectedTicket.creator_name?.charAt(0) || 'C'}
                </span>
              </div>
              <div>
                <p className="font-medium text-[#1a1a2e] text-sm">{selectedTicket.creator_name}</p>
                <p className="text-xs text-neutral-500">
                  {formatDateTime(selectedTicket.created_at)}
                </p>
              </div>
            </div>
            <p className="text-neutral-700 whitespace-pre-wrap">{selectedTicket.description}</p>
            {selectedTicket.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedTicket.attachments.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-100 rounded-xl text-sm text-blue-600 hover:bg-neutral-200"
                  >
                    <Paperclip className="w-3 h-3" />
                    Attachment {idx + 1}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          {selectedTicket.messages?.map((msg, index) => (
            <div
              key={index}
              className={`max-w-3xl ${msg.sender_type === 'client' ? '' : 'ml-auto'}`}
            >
              <div className={`rounded-xl p-4 shadow-soft border ${
                msg.sender_type === 'admin' 
                  ? 'bg-pink-50 border-pink-200' 
                  : 'bg-white border-blue-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    msg.sender_type === 'admin' ? 'bg-pink-200' : 'bg-blue-100'
                  }`}>
                    <span className={`font-medium text-sm ${
                      msg.sender_type === 'admin' ? 'text-pink-600' : 'text-blue-600'
                    }`}>
                      {msg.sender_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a2e] text-sm">
                      {msg.sender_name}
                      {msg.sender_type === 'admin' && (
                        <span className="ml-2 text-xs text-pink-600 bg-pink-100 px-2 py-0.5 rounded-full">Support Team</span>
                      )}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {formatDateTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
                <p className="text-neutral-700 whitespace-pre-wrap">{msg.message}</p>
                {msg.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.attachments.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-100 rounded-xl text-sm text-blue-600 hover:bg-neutral-200"
                      >
                        <Paperclip className="w-3 h-3" />
                        Attachment {idx + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Input */}
        {selectedTicket.status !== 'closed' && (
          <div className="bg-white border-t border-white/40 p-4">
            <form onSubmit={handleSendMessage} className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500 resize-none"
                  rows={2}
                />
              </div>
              <input
                type="file"
                id="client-chat-file-upload"
                className="hidden"
                multiple
                onChange={(e) => handleFileUpload(e, true)}
                accept="image/*,.pdf,.doc,.docx"
              />
              <label
                htmlFor="client-chat-file-upload"
                className={`p-3 rounded-xl border border-white/40 hover:bg-neutral-100 cursor-pointer transition-colors ${uploading ? 'opacity-50' : ''}`}
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                ) : (
                  <Image className="w-5 h-5 text-neutral-500" />
                )}
              </label>
              <button
                type="submit"
                disabled={sending || (!newMessage.trim())}
                className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Help Desk</h1>
          <p className="text-neutral-600 mt-1">Get support from our team</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#3366FF] to-[#3366FF] text-white rounded-xl hover:opacity-90 transition-opacity shadow-lg"
        >
          <Plus className="w-5 h-5" />
          New Ticket
        </button>
      </div>

      {/* Create Ticket Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white  shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-white/40 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#1a1a2e]">Create New Ticket</h2>
              <button onClick={() => setShowCreateForm(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief summary of your issue"
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  rows={5}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500 resize-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Attachments</label>
                <div className="border-2 border-dashed border-white/40 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="client-ticket-file-upload"
                    className="hidden"
                    multiple
                    onChange={(e) => handleFileUpload(e, false)}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <label htmlFor="client-ticket-file-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="w-8 h-8 mx-auto text-blue-500 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 mx-auto text-neutral-400" />
                    )}
                    <p className="mt-2 text-sm text-neutral-600">Click to upload images or documents</p>
                  </label>
                </div>
                {formData.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.attachments.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-xl">
                        <Paperclip className="w-3 h-3 text-blue-600" />
                        <span className="text-sm text-blue-700">File {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            attachments: prev.attachments.filter((_, i) => i !== idx)
                          }))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
                >
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-white/40">
          <p className="text-2xl font-bold text-[#1a1a2e]">{tickets.length}</p>
          <p className="text-sm text-neutral-600">Total Tickets</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <p className="text-2xl font-bold text-yellow-600">{tickets.filter(t => t.status === 'open').length}</p>
          <p className="text-sm text-yellow-700">Open</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <p className="text-2xl font-bold text-blue-600">{tickets.filter(t => t.status === 'in_progress').length}</p>
          <p className="text-sm text-blue-700">In Progress</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <p className="text-2xl font-bold text-green-600">{tickets.filter(t => t.status === 'resolved').length}</p>
          <p className="text-sm text-green-700">Resolved</p>
        </div>
      </div>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
          <h3 className="text-xl font-semibold text-neutral-700 mb-2">No tickets yet</h3>
          <p className="text-neutral-500 mb-6">Create a ticket to get help from our team</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
          >
            Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <div
              key={ticket._id}
              onClick={() => fetchTicketDetails(ticket._id)}
              className="glass-card p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#1a1a2e]">{ticket.subject}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 line-clamp-2">{ticket.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(ticket.created_at)}
                    </span>
                    {ticket.messages?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {ticket.messages.length} replies
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
